/**
 * useOkrRenderers.ts
 *
 * Registers CopilotKit v2 tool renderers for all OneThing OKR tools.
 * Call inside CopilotToolRenderers (inside <CopilotKitProvider>). Returns nothing.
 */

import { useRenderTool } from "@copilotkit/react-core/v2";
import { createElement } from "react";
import { ObjectiveListCard, type ObjectiveListRow } from "../cards/ObjectiveListCard";
import { KeyResultListCard, type KeyResultListRow } from "../cards/KeyResultListCard";
import { ObjectiveDetailCard, type ObjectiveDetail } from "../cards/ObjectiveDetailCard";
import { MutationResultCard, type MutationData } from "../cards/MutationResultCard";
import {
  type ToolRenderProps,
  LoadingCard,
  ErrorCard,
  RawCard,
  resolveEnvelope,
} from "./renderHelpers";

// ---------------------------------------------------------------------------
// Render helpers — one per tool
// ---------------------------------------------------------------------------

function renderObjectiveList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading objectives…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "objective_list") {
    return createElement(ObjectiveListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as ObjectiveListRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderKeyResultList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading key results…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "key_result_list") {
    return createElement(KeyResultListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as KeyResultListRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// manage_objective emits either objective_detail (get) or objective_mutation (write)
function renderManageObjective(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading objective…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok) {
    if (env.kind === "objective_detail") {
      return createElement(ObjectiveDetailCard, { data: env.data as ObjectiveDetail });
    }
    if (env.kind === "objective_mutation") {
      return createElement(MutationResultCard, {
        kind: "objective_mutation",
        summary: env.summary,
        data: env.data as MutationData,
      });
    }
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// manage_key_result emits key_result_mutation
function renderManageKeyResult(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading key result…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "key_result_mutation") {
    return createElement(MutationResultCard, {
      kind: "key_result_mutation",
      summary: env.summary,
      data: env.data as MutationData,
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// manage_ppp emits ppp_mutation (create / update / reorder)
function renderManagePpp(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Updating PPP…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "ppp_mutation") {
    return createElement(MutationResultCard, {
      kind: "ppp_mutation",
      summary: env.summary,
      data: env.data as MutationData,
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOkrRenderers(): void {
  // T04 — list objectives
  useRenderTool({ name: "onething__list_objectives", render: renderObjectiveList }, []);
  // T05 — list key results
  useRenderTool({ name: "onething__list_key_results", render: renderKeyResultList }, []);
  // T06 — manage objective (get → detail card; write → mutation card)
  useRenderTool({ name: "onething__manage_objective", render: renderManageObjective }, []);
  // T07 — manage key result (write → mutation card)
  useRenderTool({ name: "onething__manage_key_result", render: renderManageKeyResult }, []);
  // manage PPP → ppp_mutation
  useRenderTool({ name: "onething__manage_ppp", render: renderManagePpp }, []);
}
